import { FilterProductsService } from './../../product/services/filter-products.service'
import {
  CategoryPageResultsEvent, NavigationEvent, ProductDetailsPageEvent, ProductPageEventBuilder, SearchPageResultsEvent
} from '@spartacus/storefront'
import { Injectable } from '@angular/core'
import { filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators'
import { createFrom, EventService, PageType, ProductSearchService, ProductService } from '@spartacus/core'
import { SearchPageZeroResultsEvent } from './search-page-zero-results.event'
import { ProductFiltersEvent } from './product-filters.event'
import { ProductVariantFilterData } from '../../models'

@Injectable({
  providedIn: 'root'
})
export class MbProductPageEventBuilder extends ProductPageEventBuilder {

  constructor (protected eventService: EventService,
              protected productService: ProductService,
              protected productSearchService: ProductSearchService,
              protected filterProductsService: FilterProductsService) {
    super(eventService, productService, productSearchService)
  }

  protected register () {
    this.eventService.register(
      SearchPageResultsEvent,
      this.buildSearchPageResultsEvent()
    )
    this.eventService.register(
      ProductDetailsPageEvent,
      this.buildProductDetailsPageEvent()
    )
    this.eventService.register(
      CategoryPageResultsEvent,
      this.buildCategoryResultsPageEvent()
    )
    this.eventService.register(
      SearchPageZeroResultsEvent,
      this.buildSearchPageZeroResultsEvent()
    )
    this.eventService.register(
      ProductFiltersEvent,
      this.buildProductFiltersEvent()
    )
  }

  protected buildProductDetailsPageEvent () {
    return this.eventService.get(NavigationEvent).pipe(
      filter(navigationEvent => navigationEvent.semanticRoute === 'product'),
      filter(p => !p.url.includes(p.previousUrl || '\\')), // hack it 'till you make it. For some reason the event is emitted 2x also
                                                           // sometimes same url with hash
      switchMap(navigationEvent => this.productService.get(navigationEvent.context.id).pipe(
        filter(product => Boolean(product)),
        take(1),
        map(product => createFrom(ProductDetailsPageEvent, {
            navigation: navigationEvent,
            categories: product?.categories,
            code: product?.code,
            name: product?.name,
            price: product?.price,
            variants: product?.variantOptions
          })
        ),
      ))
    )
  }

  protected buildSearchPageResultsEvent () {
    return this.eventService.get(NavigationEvent).pipe(
      switchMap(navigation => this.productSearchService.getResults().pipe(
        filter(searchResults => Boolean(searchResults.freeTextSearch?.length)),
        map(searchResults => createFrom(SearchPageResultsEvent, {
          navigation,
          ...{
            searchTerm: searchResults.freeTextSearch || '',
            numberOfResults: searchResults.pagination?.totalResults || 0,
            products: searchResults.products
          },
        })),
      )),
    )
  }

  protected buildSearchPageZeroResultsEvent () {
    return this.eventService.get(NavigationEvent).pipe(
      switchMap(navigation => this.productSearchService.getResults().pipe(
        filter(searchResults => Boolean(!searchResults.products?.length) && Boolean(searchResults.freeTextSearch?.length)),
        map(searchResults => createFrom(SearchPageZeroResultsEvent, {
          navigation,
          ...{
            searchTerm: searchResults.freeTextSearch || ''
          },
        })),
      )),
    )
  }

  protected buildCategoryResultsPageEvent () {
    return this.eventService.get(NavigationEvent).pipe(
      filter(navigationEvent => navigationEvent.semanticRoute === undefined && navigationEvent.context.type === PageType.PRODUCT_PAGE),
      switchMap(navigation => this.productSearchService.getResults().pipe(
        filter(searchResults => Boolean(searchResults.products?.length)),
        map(searchResults => createFrom(CategoryPageResultsEvent, {
            navigation,
            ...{
              categoryCode: navigation?.context?.id,
              numberOfResults: searchResults?.pagination?.totalResults || 0,
              categoryName: searchResults.breadcrumbs?.[0]?.facetValueName,
              products: searchResults.products
            },
          })
        ),
      ))
    )
  }

  protected buildProductFiltersEvent () {
    return this.eventService.get(NavigationEvent).pipe(
      filter(navigationEvent => navigationEvent.semanticRoute === 'product'),
      filter(p => !p.url.includes(p.previousUrl || '\\') && !!p.previousUrl?.includes('products')),
      switchMap(navigationEvent => this.filterProductsService.filteredFacets$.pipe(
        take(2),
        filter(facets => this.getConjugateAndSize(facets)?.[1] !== undefined),
        withLatestFrom(this.productService.get(navigationEvent.context.id)),
        map(([facets, product]) => createFrom(ProductFiltersEvent, {
            navigation: navigationEvent,
            eventLabel: product?.name,
            eventAction: this.getConjugateAndSize(facets).toString().replace(',', '; ')
          })
        ),
      ))
    )
  }

  protected getConjugateAndSize (facets: ProductVariantFilterData[]) {
    return facets?.map((facet: ProductVariantFilterData) => facet?.filterOptions?.find((option: any) => option?.active)?.name)
  }
}
